import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { getQuote } from '../api';
import { NVLogo } from './Brand';

// ------- tiny pixel chip logo -------
export const ChipLogo = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ imageRendering: 'pixelated' }} aria-hidden>
    <rect x="3" y="3" width="18" height="18" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
    <rect x="7" y="7" width="10" height="10" fill="#eac54f" stroke="#2a1a0e" strokeWidth="1.2" />
    <rect x="10" y="10" width="4" height="4" fill="#8d6bb8" />
    {[5, 9, 13, 17].map((p) => (
      <React.Fragment key={p}>
        <rect x={p} y="0.5" width="2" height="3" fill="#d9c6a5" />
        <rect x={p} y="20.5" width="2" height="3" fill="#d9c6a5" />
        <rect x="0.5" y={p} width="3" height="2" fill="#d9c6a5" />
        <rect x="20.5" y={p} width="3" height="2" fill="#d9c6a5" />
      </React.Fragment>
    ))}
  </svg>
);

// ------- window frame -------
export const PxWindow = ({ title, children, className = '', tape = false, dark = false, right = null }) => (
  <div className={`px-window ${className}`} style={dark ? { background: 'var(--nv-night-2)' } : undefined}>
    <div className="px-window-title">
      <span>{title}</span>
      {right || (
        <span className="px-window-dots"><i /><i /><i /></span>
      )}
    </div>
    {tape && <div className="px-tape-strip" />}
    {children}
  </div>
);

// ------- navbar (live ETH quote from the ledger) -------
const QuoteValue = ({ loaded, quote }) => {
  if (!loaded) return <>…</>;
  if (quote === null) return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>no signal</span>;
  return <>{`$${Math.round(quote).toLocaleString()}`}</>;
};

export const Navbar = () => {
  const { pathname } = useLocation();
  const [quote, setQuote] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      const q = await getQuote();
      if (alive) { setQuote(q); setLoaded(true); }
    };
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const linkStyle = (active) => (active ? { color: 'var(--nv-ink)', textDecoration: 'underline' } : undefined);

  return (
    <div className="nv-nav-wrap">
      <nav className="nv-nav">
        <Link to="/" className="nv-nav-logo">
          <NVLogo size={32} />
          <span className="font-pixel" style={{ fontSize: 13, letterSpacing: 2 }}>
            NANO<span style={{ color: 'var(--nv-brown)' }}>VRAM</span>
          </span>
        </Link>
        <div className="nv-nav-links">
          <Link to="/rent" style={linkStyle(pathname === '/rent')}>Rent</Link>
          <Link to="/wire" style={linkStyle(pathname.startsWith('/wire'))}>Wire</Link>
          <Link to="/agent" style={linkStyle(pathname === '/agent')}>Byte</Link>
          <Link to="/docs" style={linkStyle(pathname === '/docs')}>Docs</Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="nv-eth-badge" title="ETH quote — the ledger's word, refreshed every minute" data-testid="eth-quote-badge">
            <span className="glyph">◆</span>
            <QuoteValue loaded={loaded} quote={quote} />
          </span>
          <Link to="/rent" className="px-btn sm nv-nav-cta" data-testid="nav-rent-btn">Rent a GPU</Link>
          <button
            className="nv-nav-burger"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="nav-burger-btn"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      {menuOpen && (
        <div className="nv-nav-drawer" data-testid="mobile-nav-drawer">
          <Link to="/rent" style={linkStyle(pathname === '/rent')} data-testid="mobile-nav-rent">Rent</Link>
          <Link to="/wire" style={linkStyle(pathname.startsWith('/wire'))} data-testid="mobile-nav-wire">Wire</Link>
          <Link to="/agent" style={linkStyle(pathname === '/agent')} data-testid="mobile-nav-byte">Byte</Link>
          <Link to="/docs" style={linkStyle(pathname === '/docs')} data-testid="mobile-nav-docs">Docs</Link>
        </div>
      )}
    </div>
  );
};

// ------- pixel board (hero art) -------
export const PixelBoard = () => (
  <svg className="nv-board-svg" viewBox="0 0 640 340" role="img" aria-label="the NANOVRAM board">
    <rect x="12" y="18" width="616" height="304" rx="14" fill="#a06b3d" stroke="#2a1a0e" strokeWidth="5" />
    <rect x="26" y="32" width="588" height="276" rx="8" fill="#8a5a33" stroke="#2a1a0e" strokeWidth="3" />
    {[70, 120, 170, 220, 270].map((y) => (
      <path key={y} d={`M40 ${y} H180 V${y + 26} H320`} fill="none" stroke="#eac54f" strokeWidth="3" opacity="0.5" />
    ))}
    <rect x="150" y="40" width="64" height="250" fill="#3a2717" stroke="#2a1a0e" strokeWidth="4" />
    <rect x="162" y="52" width="40" height="210" fill="#211510" stroke="#2a1a0e" strokeWidth="3" />
    <text x="182" y="170" fill="#d9c6a5" fontFamily="'Press Start 2P', monospace" fontSize="12" transform="rotate(90 182 170)" textAnchor="middle">nanoVRAM</text>
    <rect x="300" y="180" width="110" height="110" fill="#efe5cf" stroke="#2a1a0e" strokeWidth="4" />
    <rect x="322" y="202" width="66" height="66" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="3" />
    <rect x="344" y="224" width="10" height="10" fill="#e05a4e" />
    <rect x="356" y="224" width="10" height="10" fill="#eac54f" />
    <rect x="344" y="236" width="10" height="10" fill="#4caf6d" />
    <rect x="356" y="236" width="10" height="10" fill="#8d6bb8" />
    <text x="355" y="305" fill="#2a1a0e" fontFamily="'Press Start 2P', monospace" fontSize="9" textAnchor="middle">nanoVRAM</text>
    <rect x="470" y="60" width="120" height="70" fill="#efe5cf" stroke="#2a1a0e" strokeWidth="4" />
    <rect x="486" y="74" width="88" height="42" fill="#3a2717" stroke="#2a1a0e" strokeWidth="3" />
    {[492, 506, 520, 534, 548, 562].map((x) => (
      <rect key={x} x={x} y="80" width="6" height="30" fill="#eac54f" opacity="0.8" />
    ))}
    <rect x="470" y="210" width="90" height="56" fill="#2f6f6a" stroke="#2a1a0e" strokeWidth="4" />
    <rect x="484" y="224" width="62" height="28" fill="#211510" stroke="#2a1a0e" strokeWidth="3" />
    {[54, 66, 78, 90, 102, 114, 126].map((x) => (
      <rect key={x} x={x} y="296" width="8" height="14" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="2" />
    ))}
    {[560, 572, 584, 596].map((x) => (
      <rect key={x} x={x} y="296" width="8" height="14" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="2" />
    ))}
    {[[30, 36], [598, 36], [30, 292], [598, 292]].map(([cx, cy]) => (
      <circle key={`${cx}-${cy}`} cx={cx + 6} cy={cy + 6} r="7" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="3" />
    ))}
  </svg>
);

// ------- footer -------
export const Footer = () => (
  <footer className="nv-footer nv-stars">
    <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 22 }}>
        <NVLogo size={34} />
        <span className="font-pixel" style={{ fontSize: 13, color: 'var(--nv-cream)', letterSpacing: 2 }}>
          NANO<span style={{ color: 'var(--nv-gold)' }}>VRAM</span>
        </span>
      </div>
      <p className="prompt">NV8&gt; read warning.rom<span className="nv-cursor" /></p>
      <div className="warn">
        The mint id on this desk is the only $NVRM · anything else wearing the name is a counterfeit · verify the card, then verify the ledger
      </div>
      <p style={{ marginTop: 34, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6 }}>
        power off anyone who says otherwise
      </p>
      <p style={{ marginTop: 18 }}>
        <Link to="/brand" style={{ color: 'var(--nv-gold)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }} data-testid="footer-brand-link">
          brand rom → logo &amp; banner
        </Link>
        <span style={{ color: 'var(--nv-tan)', opacity: 0.4, margin: '0 10px' }}>·</span>
        <Link to="/docs" style={{ color: 'var(--nv-gold)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }} data-testid="footer-docs-link">
          docs rom → how it works
        </Link>
      </p>
    </div>
  </footer>
);
